import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Get } from '../hooks/useFetch';
import { toStateCode } from '../util/usPlaces';

// Street address with Google-style suggestions (Settings → Integrations →
// Address autocomplete; API: addressautocomplete.go, Geoapify). Suggestions
// follow the COUNTRY PICKED ON THE FORM — not where the shopper is. Picking
// one fills street, city, state and ZIP through onPick. When the store has it
// off, the free daily quota is used up, or nothing matches, this is a plain
// input.

const PIN = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none', marginTop: 3, opacity: 0.5 }}>
  <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" />
</svg>;

const AddressAutocomplete = ({ enabled, country, value, onChange, onPick, inputStyle, dark = false, placeholder = 'Street address' }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const seq = useRef(0);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { setItems([]); }, [country]);

  const search = (q) => {
    clearTimeout(timer.current);
    if (!enabled || String(q).trim().length < 3) { setItems([]); return; }
    timer.current = setTimeout(() => {
      const my = ++seq.current;
      const cc = String(country || 'US').toUpperCase();
      Get(`/api/v1/public/address-autocomplete?q=${encodeURIComponent(q.trim())}&country=${encodeURIComponent(cc)}`, (res) => {
        if (my !== seq.current) return; // a newer keystroke won
        setItems(Array.isArray(res?.suggestions) ? res.suggestions : []);
        setHi(0);
      }, () => { if (my === seq.current) setItems([]); });
    }, 300);
  };

  const pick = (s) => {
    const us = String(s.country || country || 'US').toUpperCase() === 'US';
    onPick({
      address: s.line1 || '',
      city: s.city || '',
      // US forms store the 2-letter code (the State dropdown + ShipStation); elsewhere the region name.
      state: us ? (toStateCode(s.stateCode) || toStateCode(s.state) || s.stateCode || '') : (s.state || s.stateCode || ''),
      zip: s.zip || '',
      stateName: s.state || '',
    });
    setItems([]); setOpen(false);
  };

  const show = open && items.length > 0;
  const bg = dark ? '#1a1d22' : '#fff';
  return <div style={{ position: 'relative', width: '100%' }}>
    <input style={inputStyle} placeholder={placeholder} value={value} autoComplete={enabled ? 'off' : 'street-address'}
      role={enabled ? 'combobox' : undefined} aria-expanded={enabled ? show : undefined} aria-autocomplete={enabled ? 'list' : undefined}
      onChange={(e) => { onChange(e.target.value); setOpen(true); search(e.target.value); }}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={(e) => {
        if (!show) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, items.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); pick(items[hi]); }
        else if (e.key === 'Escape') setOpen(false);
      }} />
    {show && <div role="listbox" style={{
      position: 'absolute', zIndex: 45, left: 0, right: 0, top: 'calc(100% + 4px)', background: bg, overflow: 'hidden',
      border: dark ? '1px solid rgba(255,255,255,.18)' : '1px solid rgba(0,0,0,.14)', borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.16)',
    }}>
      {items.map((s, i) => {
        const rest = String(s.label || '').startsWith(s.line1) ? String(s.label).slice(String(s.line1).length).replace(/^[,\s]+/, '') : s.label;
        return <div key={s.label} role="option" aria-selected={i === hi}
          onMouseDown={(e) => { e.preventDefault(); pick(s); }} onMouseEnter={() => setHi(i)}
          style={{ display: 'flex', gap: 9, padding: '9px 12px', cursor: 'pointer', fontSize: 14, lineHeight: 1.35, color: inputStyle?.color,
            background: i === hi ? (dark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.05)') : 'transparent',
            borderTop: i ? (dark ? '1px solid rgba(255,255,255,.06)' : '1px solid rgba(0,0,0,.05)') : 'none' }}>
          {PIN}
          <span style={{ minWidth: 0 }}>
            <b style={{ fontWeight: 600 }}>{s.line1}</b>{rest ? <span style={{ opacity: 0.65 }}> {rest}</span> : null}
          </span>
        </div>;
      })}
      {/* Geoapify's free plan requires this attribution. */}
      <div style={{ textAlign: 'right', fontSize: 11, padding: '5px 10px', opacity: 0.6, color: inputStyle?.color }}>
        Powered by <a href="https://www.geoapify.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }} onMouseDown={(e) => e.preventDefault()}>Geoapify</a>
      </div>
    </div>}
  </div>;
};
AddressAutocomplete.propTypes = {
  enabled: PropTypes.bool, country: PropTypes.string, value: PropTypes.string, onChange: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired, inputStyle: PropTypes.object, dark: PropTypes.bool, placeholder: PropTypes.string,
};

export default AddressAutocomplete;
